// Vue
import { i18n } from "@/i18n"

import type { IFeature } from "@/types/Users.types"
import { defineStore } from "pinia"
import type { IEstablishment } from "@/types/Establishment.types"

import AccountDataService from "@/services/AccountDataService"
import { useAuthenticationStore } from "./authentication"
import { useAlertStore } from "./alert"
import { useEstablishmentStore } from "./establishment"
import { AlertType } from "@/static/Alert"
import { ConfigurationState } from "@/static/Configuration"
import type { IAccountForm, IGoogleButton, IPlan } from "@/types/Account.types"
import SubscriptionDataService from "@/services/SubscriptionDataService"
import type IContactForm from "@/types/Contact.types"
import OnboardingDataService from "@/services/OnboardingDataService"
import type { IMenuItem } from "@/types/Menu.types"
import GoogleDataService from "@/services/GoogleDataService"
import FacebookDataService from "@/services/FacebookDataService";

export const useAccountStore = defineStore("account", {
  state: () => ({
    reload: 0,
    establishments: [] as IEstablishment[],
    features: [] as IFeature[],

    /** @type {Boolean | null} current authenticated user first attempt to the app */
    firstAttempt: localStorage.getItem("firstAttempt") as string | null,

    productPlans: [] as IPlan[],

    /**
     * Is the current account has at least one premium establishment ?
     */
    hasPremium: false,

    /**
     * Is the current user linked to at least one destination provider (fiche Dahub) ?
     * Conditionne l'affichage du switch Ma visibilité / Mes échanges.
     */
    hasDestinationProviders: false,

    isLoading: true,
    establishmentLoaded: false,
    hasLoadedMenu: false,

    menuItems: [] as IMenuItem[],

    googleAuthUrl: null,
  }),

  getters: {
    isFirstAttempt: (state): {} => {
      return state.firstAttempt === "true" || state.firstAttempt === null
    },
  },

  actions: {
    clear() {
      this.establishments = [] as IEstablishment[]
      this.features = [] as IFeature[]
      this.firstAttempt = localStorage.getItem("firstAttempt") as string | null
      this.productPlans = [] as IPlan[]
      this.hasDestinationProviders = false
      this.isLoading = true
    },

    setFirstAttempt(bool: string) {
      localStorage.setItem("firstAttempt", bool)
      this.firstAttempt = bool
    },

    /**
     * Create new account
     * @param {IAccountForm} form
     * @param contactToken
     * @param huboAccountId
     * @param successCallback
     */
    create(
      form: IAccountForm,
      contactToken: string,
      huboAccountId: number,
      successCallback: (authData: { access_token: string; token_type: string }, accountId: number) => void,
      errorCallback?: () => void
    ) {
      const authStore = useAuthenticationStore()

      AccountDataService.createAccount(form, contactToken, huboAccountId).then(
        (res) => {
          authStore.email = form.email
          const accountId = res.data.data.id

          // If contactToken exists, start configuration onboarding
          if (contactToken) {
            this.updateState(ConfigurationState.START_ONBOARDING, accountId)
          }
          // If tracking not exists, start configuration at the onboarding
          else {
            this.updateState(ConfigurationState.START_ONBOARDING, accountId)
          }

          // The first time the user of new account login, show the welcome page
          this.setFirstAttempt("true")

          const alertStore = useAlertStore()
          alertStore.push(AlertType.success, i18n.global.t("common.messages.account_created_successfully"))

          successCallback(res.data.auth_data as { access_token: string; token_type: string }, accountId)
        },
        (e) => {
          const alertStore = useAlertStore()
          switch (e.response.status) {
            case 401:
              break
            case 422:
              authStore.authenticationError(e.response.code, () => {
                alertStore.push(AlertType.error, i18n.global.t("common.errors.email_already_in_used"), e.message)
              })
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
          if (errorCallback) {
            errorCallback()
          }
        }
      )
    },

    /**
     * Get Google button url for sign in or sign up
     * @param {AccountType} accountType
     * @param {GoogleAccountType} connectType
     * @param token
     * @param id
     * @param successCallback
     */
    googleButtonUrl(data: IGoogleButton, successCallback?: () => void) {
      GoogleDataService.getAuthenticationUrl(data).then(
        (res) => {
          this.googleAuthUrl = res.data.auth_url
          if (successCallback) {
            successCallback()
          }
        },
        (e) => {
          const authStore = useAuthenticationStore()
          const alertStore = useAlertStore()

          switch (e.response.status) {
            case 401:
              break
            case 422:
              authStore.authenticationError(e.response.code, () => {
                alertStore.push(AlertType.error, i18n.global.t("common.errors.email_already_in_used"), e.message)
              })
              break
            case 426:
              this.router.push({ name: "error-version" })
              alertStore.push(AlertType.info, "Merci de patienter encore quelques instants...")
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    /**
     * Authenticate or create account with Google
     * If account existing but with a password, google authentication will be automatically created
     * @param code
     * @param state
     * @param origin
     */
    googleAuth(code: string, state: string, origin: string) {
      const authenticationStore = useAuthenticationStore()
      GoogleDataService.authenticateWithGoogle({
        code: code,
        state: state,
        email: authenticationStore.user ? authenticationStore.user.email : null,
        origin: origin
      }).then(
        (res) => {
          const response = res.data
          this.setFirstAttempt("false")

          if (response.auth_data) {
            const authUser = response.auth_data as {
              access_token: string
              token_type: string
              expires_in: string
            }

            if (response.data) {
              // c'était une création (sign_up)
              const accountData = response.data as {
                hubo_account_id: string
                hubo_contact_token: string
                id: number
              }

              this.updateState(ConfigurationState.START_ONBOARDING, accountData.id)

              // The first time the user of new account login, show the welcome page
              this.setFirstAttempt("true")

              const alertStore = useAlertStore()
              alertStore.push(AlertType.success, i18n.global.t("common.messages.account_created_successfully"))
            }

            // LogIn l'utilisateur

            authenticationStore.storeLoginUser(authUser.access_token, authUser.token_type, this.firstAttempt === "true")
          } else {
            // return error
            const alertStore = useAlertStore()
            alertStore.push(AlertType.error, i18n.global.t("common.errors.default"))
            this.router.go(-2)
          }
        },
        (e) => {
          const alertStore = useAlertStore()

          switch (e.response.status) {
            case 401:
              break
            case 400:
              if (e.response.data.message === "CONTACT_TOKEN_ALREADY_USED") {
                // User tried to create account for a Hubo contact token existing (he has already an account with another email)
                alertStore.push(AlertType.info, i18n.global.t("common.errors.account_already_exist"))
                this.router.push({ name: "auth" })
              } else if (e.response.data.message === "NEED_REVOKE") {
                let message = i18n.global.t("common.errors.need_revoke")
                let urlMsg = i18n.global.t("common.errors.need_revoke_url")

                message += "<a href='https://myaccount.google.com/connections'  target='_blank'>" + urlMsg + '</a>'

                alertStore.push(AlertType.error, message)

                this.router.go(-2)
              } else {
                // Bad request (erreur sur les paramètres données dans l'endpoint /auth-url)
                alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                this.router.go(-2)
              }
              break
            case 404:
              // Only for the authentication
              alertStore.push(AlertType.error, i18n.global.t("common.errors.email_unknown"))
              this.router.push({ name: "auth" })
              break
            case 500:
              // 	The given code is not valid, google responded with an error OR error while the account creation.
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              this.router.go(-2)
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              this.router.go(-2)
              break
          }
        }
      )
    },

    askGooglePermissions(code: string, state: string, origin: string, redirect_url = null) {
      const authenticationStore = useAuthenticationStore()

      return GoogleDataService.authenticateWithGoogle({
        code: code,
        state: state,
        redirect_url: redirect_url,
        email: authenticationStore.user ? authenticationStore.user.email : null,
        origin: origin
      })
    },

    askFacebookPermissions(code: string, redirect_url = null) {
      const authenticationStore = useAuthenticationStore()

      return FacebookDataService.authenticateWithFacebook({
        code: code,
        redirect_url: redirect_url,
        email: authenticationStore.user ? authenticationStore.user.email : null,
      })
    },

    /**
     * Get all current account subscriptions
     */
    getSubscriptions(successCallback?: () => void) {
      this.isLoading = true
      const alertStore = useAlertStore()
      const auth = useAuthenticationStore()

      SubscriptionDataService.getSubscriptions(auth.currentHeaders).then(
        (res) => {
          this.productPlans = res.data.data

          this.isLoading = false

          if (successCallback) {
            successCallback()
          }
        },
        (e) => {
          this.isLoading = false
          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    /**
     * Get all authenticated account access features
     * @param callback
     */
    getFeatures(callback: (features: any) => void) {
      const authStore = useAuthenticationStore()

      AccountDataService.features(authStore.currentHeaders).then(
        (res) => {
          this.features = res.data.data
          callback(res.data.data)
        },
        (e) => {
          const alertStore = useAlertStore()
          switch (e.response.status) {
            case 401:
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    /**
     * Get all authenticated account establishments
     * @param callback
     * @param showError
     * @param preserveCurrentEstablishment Si true, ne pas modifier currentEstablishment (ex: routes sales)
     */
    getEstablishments(callback: (establishments: IEstablishment[]) => void, showError?: Boolean, preserveCurrentEstablishment?: boolean) {
      const authStore = useAuthenticationStore()

      if (showError === null || showError === undefined) {
        showError = true
      }

      if (authStore.currentHeaders.Authorization && authStore.user?.email) {
        const establishmentStore = useEstablishmentStore()
        establishmentStore.isLoading = true
        return AccountDataService.establishments(authStore.currentHeaders).then(
          (res) => {
            const etabs = [] as IEstablishment[]
            res.data.data.establishments.forEach((establishment: IEstablishment) => {
              if (establishment) {
                etabs.push(establishment)
              }
            })
            this.establishments = etabs
            this.hasPremium = res.data.data.has_premium
            this.hasDestinationProviders = res.data.data.has_destination_providers === true

            const ids = etabs.map((etab) => etab.id)

            // Sur les routes sales, ne pas modifier currentEstablishment (l'utilisateur a peut-être
            // sélectionné un établissement qu'il consulte et ne doit pas être réinitialisé au retour)
            if (!preserveCurrentEstablishment) {
              // Vérifier s'il y a un établissement spécifiquement demandé dans sessionStorage
              const requestedEstablishmentId = sessionStorage.getItem("requestedEstablishmentId")

              // Si un établissement spécifique a été demandé, toujours l'utiliser en priorité
              if (requestedEstablishmentId) {
                const requestedEstab = etabs.find((etab) => etab.id === Number(requestedEstablishmentId))
                if (requestedEstab) {
                  establishmentStore.currentEstablishment = requestedEstab
                }
                // Ne PAS supprimer requestedEstablishmentId ici, c'est fait à la fin du flux dans storeLoginUser
              }
              // Sinon, cas du login as qui ne supprime pas le currentEstablishment (on se retrouve avec l'établissement d'un autre compte)
              else if (
                establishmentStore.currentEstablishment === undefined ||
                (establishmentStore.currentEstablishment.id !== null &&
                  !ids.includes(establishmentStore.currentEstablishment.id))
              ) {
                establishmentStore.currentEstablishment = etabs[0]
              } else {
                const currentId = establishmentStore.currentEstablishment?.id
                const freshEstablishment = currentId
                  ? etabs.find((etab) => etab.id === currentId)
                  : undefined

                if (freshEstablishment) {
                  establishmentStore.currentEstablishment = freshEstablishment
                }
              }

              establishmentStore.syncLocalDiffuserBranding()
            }

            establishmentStore.isLoading = false

            callback(res.data.data.establishments)
          },
          (e) => {
            const alertStore = useAlertStore()
            switch (e.response.status) {
              case 401:
                break
              case 426:
                // Don't show errors
                break
              default:
                alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                break
            }
          }
        )
      }
    },

    /**
     * Update the current authenticated account configuration state
     * @param state
     * @param accountId
     */
    updateState(state: ConfigurationState, accountId?: Number | null) {
      const currentAccountId = this._getAccountId(accountId)

      const authStore = useAuthenticationStore()
      const alertStore = useAlertStore()

      // If currentAccountId not find, don't update state
      if (currentAccountId) {
        AccountDataService.updateConfigurationState(currentAccountId, state).then(
          (res) => {
            if (res.data.success) {
              if (authStore.user && authStore.user!.account) {
                // Update current user account configuration state
                authStore.user!.account.configuration_state = state
              }
            } else {
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), res.data.code)
            }
          },
          (e) => {
            switch (e.response.status) {
              case 401:
                break
              default:
                alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                break
            }
          }
        )
      }
    },

    /**
     * At the end of configuration process, user account configured colonne will change to true
     * @param accountId
     */
    setAccountConfigured(configured: boolean, successCallback: () => void) {
      const currentAccountId = this._getAccountId()

      const alertStore = useAlertStore()
      const authStore = useAuthenticationStore()

      // If currentAccountId not find, don't update state
      if (currentAccountId) {
        AccountDataService.setAccountConfigured(currentAccountId, configured).then(
          () => {
            // Mettre à jour le store avec la nouvelle valeur de configured
            if (authStore.user && authStore.user.account) {
              authStore.user.account.configured = configured
              // Sauvegarder dans localStorage pour persister la mise à jour
              localStorage.setItem("user", JSON.stringify(authStore.user))
            }
            successCallback()
          },
          (e) => {
            switch (e.response.status) {
              case 401:
                break
              default:
                alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                break
            }
          }
        )
      } else {
        alertStore.push(AlertType.error, i18n.global.t("common.errors.no_account_id"))
      }
    },

    /**
     * Get the current connected account id if no account id is given
     * @private
     * @param accountId
     * @returns
     */
    _getAccountId(accountId?: Number | null): Number | null {
      let currentAccountId = null
      const authStore = useAuthenticationStore()

      // If currentAccountId is given, then get it
      if (accountId) {
        currentAccountId = accountId
      }
      // If currentAccountId not given AND authenticated user exists, then get authenticated user account id
      else if (authStore.user && authStore.user.account) {
        currentAccountId = authStore.user.account.id
      }

      return currentAccountId
    },

    contactHubspot(form: IContactForm, successCallback: () => void) {
      OnboardingDataService.sendContactRequest(form).then(
        (res) => {
          successCallback()
        },
        (e) => {
          const authStore = useAuthenticationStore()
          const alertStore = useAlertStore()
          switch (e.response.status) {
            case 401:
              break
            case 500:
              {
                alertStore.push(AlertType.error, e.response.data.message, i18n.global.t("common.errors.default"))
                const token = e.response.data.data.token
                this.router.push({ name: "auth", query: { token: token } })
              }
              break
            default:
              alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },
  },

  persist: {
    enabled: true,
  },
})
